import type { ReactElement } from 'react'
import { Navigate, useLocation, type RouteObject } from 'react-router-dom'
import { FLAGS } from '@atbash-labs/ontology-flags'
import { PLANT_HREF, PLANT_ROUTE_SEGMENTS } from '@atbash-labs/plantview-links'
import { NotFoundPage } from '@axilon-apps/libs-ui-dashboard'
import AppLayout from '../components/AppLayout/AppLayout'
import { CHAT_MEMORY_PATH } from '../components/Chat/chat-route'
// Chat app-context (AXI-4791): when adding/renaming a path here, also update
// `chat-app-context/routePathTrees.ts` (engineerPathTree) and
// `chat-app-context/pageContextRegistry.ts` (PAGE_CONTEXT_BY_PATH).
import GraphPage from '../pages/GraphPage/GraphPage'
import IngestPage from '../pages/IngestPage/IngestPage'
import AgentIngestPage from '../pages/AgentIngestPage/AgentIngestPage'
import TabularIngestJobPage from '../pages/TabularIngestJobPage/TabularIngestJobPage'
import TabularRunPagePage from '../pages/TabularIngestJobPage/TabularRunPagePage'
import BuildToolCallsPage from '../pages/TabularConfigPage/BuildToolCallsPage'
import TabularConfigPage from '../pages/TabularConfigPage/TabularConfigPage'
import IngestSourceDocumentPage from '../pages/IngestSourceDocumentPage/IngestSourceDocumentPage'
import SettingsPage from '../pages/SettingsPage/SettingsPage'
import { UsersPage } from '../pages/UsersPage/UsersPage'
import MergeReviewPage from '../pages/MergeReviewPage/MergeReviewPage'
import MergesPage from '../pages/MergesPage/MergesPage'
import ConfigurePage from '../pages/ConfigurePage/ConfigurePage'
import { ConfigsRedirect } from '../pages/ConfigurePage/legacy-redirects'
import RunPage from '../pages/RunPage/RunPage'
import NotificationRuleNewPage from '../pages/AgentsPage/notification-rules/NotificationRuleNewPage'
import DashboardsHomePage from '../pages/DashboardsPage/DashboardsHomePage'
import DashboardsExplorerPage from '../pages/DashboardsPage/DashboardsPage'
import TemplatesExplorerPage from '../pages/DashboardsPage/TemplatesPage'
import DashboardsAutogenPage from '../pages/DashboardsPage/DashboardsAutogenPage'
import ArtifactPage from '../pages/ArtifactPage/ArtifactPage'
import DocTemplatesExplorerPage from '../pages/DocsHubPage/DocTemplatesExplorerPage'
import DocumentsHomePage from '../pages/DocsHubPage/DocumentsHomePage'
import DocsHubPage from '../pages/DocsHubPage'
import { LegacyDocsRedirect } from '../pages/DocsHubPage/legacy-docs-redirect'
import GuidePage from '../pages/GuidePage/GuidePage'
import PrivacyPolicyPage from '../pages/LegalPage/PrivacyPolicyPage'
import TermsOfUsePage from '../pages/LegalPage/TermsOfUsePage'
import ChatPage from '../pages/ChatPage/ChatPage'
import TagsPage from '../pages/TagsPage'
import DebugRoute from '../components/DebugRoute/DebugRoute'
import GlobalAdminRoute from '../components/GlobalAdminRoute/GlobalAdminRoute'
import IngestRoute from '../components/IngestRoute/IngestRoute'
import FlagRoute from '../components/flags/FlagRoute'
import NotificationsPage from '../pages/NotificationsPage/NotificationsPage'
import PlantPage from '../pages/PlantPage/PlantPage'
import EquipmentPage from '../pages/PlantPage/EquipmentPage'
import SiteDetailPage from '../pages/PlantPage/SiteDetailPage'
import LineDetailPage from '../pages/PlantPage/LineDetailPage'
import EquipmentDetailPage from '../pages/PlantPage/EquipmentDetailPage'
import ProcessesPage from '../pages/PlantPage/ProcessesPage'
import AreasPage from '../pages/PlantPage/AreasPage'
import AreaDetailPage from '../pages/PlantPage/AreaDetailPage'
import ComputePage from '../pages/PlantPage/ComputePage'
import ComputeHostDetailPage from '../pages/PlantPage/ComputeHostDetailPage'
import QueriesPage from '../pages/PlantPage/QueriesPage'
import QueryDetailPage from '../pages/PlantPage/QueryDetailPage'
import PlcPage from '../pages/PlantPage/PlcPage'
import DcsPage from '../pages/PlantPage/DcsPage'
import NetworkPage from '../pages/PlantPage/NetworkPage'
import RoboticsPage from '../pages/PlantPage/RoboticsPage'
import SchedulesPage from '../pages/PlantPage/SchedulesPage'
import ScheduleEntityDetailPage from '../pages/PlantPage/ScheduleEntityDetailPage'
import AlarmsPage from '../pages/PlantPage/AlarmsPage'
import SafetyPage from '../pages/PlantPage/SafetyPage'
import SrsFunctionGroupDetailPage from '../pages/PlantPage/SrsFunctionGroupDetailPage'
import ProtectiveFunctionDetailPage from '../pages/PlantPage/ProtectiveFunctionDetailPage'
import LogicSolverDetailPage from '../pages/PlantPage/LogicSolverDetailPage'
import FunctionLogicDiagramDetailPage from '../pages/PlantPage/FunctionLogicDiagramDetailPage'
import SafetyPointDetailPage from '../pages/PlantPage/SafetyPointDetailPage'
import EntityResolverPage from '../pages/PlantPage/entity/EntityResolverPage'
import ModelBrowserPage from '../pages/PlantPage/model/ModelBrowserPage'
import MessagingConnectPage from '../pages/MessagingConnectPage/MessagingConnectPage'
import MessagingConnectSuccessPage from '../pages/MessagingConnectPage/MessagingConnectSuccessPage'
import { FeedbackPage } from '../pages/FeedbackPage/FeedbackPage'
import { UsageAnalyticsPage } from '../pages/UsageAnalyticsPage/UsageAnalyticsPage'
import { ReleaseHistoryPage } from '../pages/ReleaseHistoryPage/ReleaseHistoryPage'
import { PROBLEMS_PATH } from '../lib/component-links'
import ProblemsPage from '../pages/PlantPage/ProblemsPage'
import ProblemPage from '../pages/PlantPage/ProblemPage'
import TasksPage from '../pages/TasksPage/TasksPage'

function LegacyPlantProblemsRedirect(): ReactElement {
  const { search } = useLocation()
  return <Navigate to={`${PROBLEMS_PATH}${search}`} replace />
}

export const standaloneRoutes: RouteObject[] = [
  {
    path: '/integrations/connect',
    element: <MessagingConnectPage />
  },
  {
    path: '/integrations/connect/success',
    element: <MessagingConnectSuccessPage />
  }
]

const routes: RouteObject[] = [
  ...standaloneRoutes,
  {
    element: <AppLayout />,
    children: [
      {
        path: '/graph',
        element: (
          <DebugRoute fallbackHref={PLANT_HREF.root}>
            <GraphPage />
          </DebugRoute>
        )
      },
      // Plant Viewer — three tabs (Equipment / Processes / Compute), each
      // with their own drill-down routes. Path segments and URL builders
      // live in `@atbash-labs/plantview-links`; never construct a /plant/...
      // URL by hand.
      {
        path: '/plant',
        element: <PlantPage />,
        children: [
          { index: true, element: <Navigate to={PLANT_HREF.equipmentIndex} replace /> },

          // Equipment tab: sites grid → site → line/subline → equipment item.
          { path: PLANT_ROUTE_SEGMENTS.equipmentIndex, element: <EquipmentPage /> },
          { path: PLANT_ROUTE_SEGMENTS.equipmentSite, element: <SiteDetailPage /> },
          { path: PLANT_ROUTE_SEGMENTS.equipmentLine, element: <LineDetailPage /> },
          { path: PLANT_ROUTE_SEGMENTS.equipmentItem, element: <EquipmentDetailPage /> },

          // Processes tab: single page; the selected process lives in the
          // `?process=<id>` query param.
          { path: PLANT_ROUTE_SEGMENTS.processesIndex, element: <ProcessesPage /> },

          // Areas tab: spatial regions (Area) grouped by site → selected
          // area's sub-areas + LOCATED_IN children.
          { path: PLANT_ROUTE_SEGMENTS.areasIndex, element: <AreasPage /> },
          { path: PLANT_ROUTE_SEGMENTS.areaDetail, element: <AreaDetailPage /> },

          // Compute tab: network diagram → selected host.
          { path: PLANT_ROUTE_SEGMENTS.computeIndex, element: <ComputePage /> },
          { path: PLANT_ROUTE_SEGMENTS.computeHost, element: <ComputeHostDetailPage /> },

          // Queries tab: NamedQuery + query-Tag list → selected query detail.
          { path: PLANT_ROUTE_SEGMENTS.queriesIndex, element: <QueriesPage /> },
          { path: PLANT_ROUTE_SEGMENTS.queryDetail, element: <QueryDetailPage /> },

          // PLC tab: single page; the selected Controller/Program/Routine/AOI/PLCTag
          // lives in the `?node=<id>` query param.
          { path: PLANT_ROUTE_SEGMENTS.plcIndex, element: <PlcPage /> },

          // DCS tab: single page; selected DcsController/Cee/ControlModule/…
          // lives in the `?node=<id>` query param.
          {
            path: PLANT_ROUTE_SEGMENTS.dcsIndex,
            element: (
              <FlagRoute flag={FLAGS.UI_PLANT_DCS} fallbackHref={PLANT_HREF.equipmentIndex}>
                <DcsPage />
              </FlagRoute>
            )
          },

          // Network tab: single page; selected Asset/NetworkSegment/NetworkDevice
          // (Experion EMDB enterprise/PCN) lives in the `?node=<id>` query param.
          {
            path: PLANT_ROUTE_SEGMENTS.networkIndex,
            element: (
              <FlagRoute flag={FLAGS.UI_PLANT_NETWORK} fallbackHref={PLANT_HREF.equipmentIndex}>
                <NetworkPage />
              </FlagRoute>
            )
          },

          // Robotics tab: single page; selected RobotController/Arm/Job/side-node
          // lives in the `?node=<id>` query param.
          { path: PLANT_ROUTE_SEGMENTS.roboticsIndex, element: <RoboticsPage /> },

          // Schedules tab: live list of every gateway's ScheduleProfile +
          // Holiday with a live-poll active-status indicator; detail under
          // `/plant/schedules/:nodeId`.
          { path: PLANT_ROUTE_SEGMENTS.schedulesIndex, element: <SchedulesPage /> },
          { path: PLANT_ROUTE_SEGMENTS.schedulesNode, element: <ScheduleEntityDetailPage /> },

          // Alarms tab: single page; alarms grouped by UDTDefinition >
          // UDTInstance > tag path with flat-view toggle and priority
          // filter.
          { path: PLANT_ROUTE_SEGMENTS.alarmsIndex, element: <AlarmsPage /> },

          // Problems is a top-level page. Preserve old deep links.
          { path: 'problems', element: <LegacyPlantProblemsRedirect /> },

          // Safety tab: site-scoped SRS function groups + Safety Manager
          // FLDs/points. More-specific group/solver/FLD/point routes must sit
          // above `safety/:functionId`.
          {
            path: PLANT_ROUTE_SEGMENTS.safetyIndex,
            element: (
              <FlagRoute flag={FLAGS.UI_PLANT_SAFETY} fallbackHref={PLANT_HREF.equipmentIndex}>
                <SafetyPage />
              </FlagRoute>
            )
          },
          {
            path: PLANT_ROUTE_SEGMENTS.safetyGroup,
            element: (
              <FlagRoute flag={FLAGS.UI_PLANT_SAFETY} fallbackHref={PLANT_HREF.equipmentIndex}>
                <SrsFunctionGroupDetailPage />
              </FlagRoute>
            )
          },
          {
            path: PLANT_ROUTE_SEGMENTS.safetySolver,
            element: (
              <FlagRoute flag={FLAGS.UI_PLANT_SAFETY} fallbackHref={PLANT_HREF.equipmentIndex}>
                <LogicSolverDetailPage />
              </FlagRoute>
            )
          },
          {
            path: PLANT_ROUTE_SEGMENTS.safetyFld,
            element: (
              <FlagRoute flag={FLAGS.UI_PLANT_SAFETY} fallbackHref={PLANT_HREF.equipmentIndex}>
                <FunctionLogicDiagramDetailPage />
              </FlagRoute>
            )
          },
          {
            path: PLANT_ROUTE_SEGMENTS.safetyPoint,
            element: (
              <FlagRoute flag={FLAGS.UI_PLANT_SAFETY} fallbackHref={PLANT_HREF.equipmentIndex}>
                <SafetyPointDetailPage />
              </FlagRoute>
            )
          },
          {
            path: PLANT_ROUTE_SEGMENTS.safetyDetail,
            element: (
              <FlagRoute flag={FLAGS.UI_PLANT_SAFETY} fallbackHref={PLANT_HREF.equipmentIndex}>
                <ProtectiveFunctionDetailPage />
              </FlagRoute>
            )
          },

          { path: PLANT_ROUTE_SEGMENTS.entity, element: <EntityResolverPage /> },
          {
            path: PLANT_ROUTE_SEGMENTS.modelIndex,
            element: (
              <DebugRoute fallbackHref={PLANT_HREF.equipmentIndex}>
                <ModelBrowserPage />
              </DebugRoute>
            )
          },

          // Catch unknown plant sub-paths (e.g. stale `/plant/sites/...`
          // links from before the redesign) so they land on the Equipment
          // landing instead of NotFound.
          { path: '*', element: <Navigate to={PLANT_HREF.equipmentIndex} replace /> }
        ]
      },
      {
        path: '/ingest',
        element: (
          <IngestRoute>
            <IngestPage />
          </IngestRoute>
        )
      },
      {
        path: '/ingest/jobs/:jobId/agent',
        element: (
          <IngestRoute>
            <AgentIngestPage />
          </IngestRoute>
        )
      },
      {
        path: '/ingest/jobs/:jobId/tabular',
        element: (
          <IngestRoute>
            <TabularIngestJobPage />
          </IngestRoute>
        )
      },
      {
        path: '/ingest/jobs/:jobId/tabular/pages/:pageId',
        element: (
          <IngestRoute>
            <TabularRunPagePage />
          </IngestRoute>
        )
      },
      {
        path: '/ingest/configs/:name',
        element: (
          <IngestRoute>
            <TabularConfigPage />
          </IngestRoute>
        )
      },
      {
        path: '/ingest/configs/:name/builds/:buildJobId',
        element: (
          <IngestRoute>
            <TabularConfigPage />
          </IngestRoute>
        )
      },
      {
        path: '/ingest/configs/:name/builds/:buildJobId/tool-calls',
        element: (
          <IngestRoute>
            <BuildToolCallsPage />
          </IngestRoute>
        )
      },
      {
        path: '/ingest/jobs/:jobId/source-document',
        element: (
          <IngestRoute>
            <IngestSourceDocumentPage />
          </IngestRoute>
        )
      },
      // `/chat/memory` is two segments, like `/chat/:conversationId`, so the
      // literal must win or `memory` is treated as a conversation id.
      { path: '/chat', element: <ChatPage /> },
      { path: CHAT_MEMORY_PATH, element: <ChatPage /> },
      { path: '/chat/:conversationId', element: <ChatPage /> },
      {
        path: '/dashboards/autogenerate',
        element: (
          <IngestRoute>
            <DashboardsAutogenPage />
          </IngestRoute>
        )
      },
      {
        path: '/dashboards',
        element: <DashboardsHomePage />,
        children: [
          { index: true, element: <DashboardsExplorerPage /> },
          { path: 'templates', element: <TemplatesExplorerPage /> },
          { path: 'templates/f/*', element: <TemplatesExplorerPage /> },
          { path: 'templates/*', element: <TemplatesExplorerPage /> },
          { path: 'f/*', element: <DashboardsExplorerPage /> },
          { path: '*', element: <DashboardsExplorerPage /> }
        ]
      },
      {
        path: '/artifacts/:artifactId',
        element: <ArtifactPage />
      },
      // Problems and Tasks share one sidebar slot, chosen by role.
      { path: '/problems', element: <ProblemsPage /> },
      { path: '/problems/:occurrenceId', element: <ProblemPage /> },
      { path: '/tasks', element: <TasksPage /> },
      // Run pages enforce run-read access; Configure remains limited to global admins.
      {
        path: '/runs',
        element: <RunPage />
      },
      {
        path: '/configs',
        element: <ConfigsRedirect />
      },
      {
        path: '/configure',
        element: (
          <GlobalAdminRoute>
            <ConfigurePage />
          </GlobalAdminRoute>
        )
      },
      // Legacy /agents/* list routes → the generic custom-agents list.
      { path: '/agents', element: <Navigate to="/configure?type=custom" replace /> },
      {
        path: '/agents/notification-rules',
        element: <Navigate to="/configure?type=notification-rules" replace />
      },
      { path: '/agents/notification-rules/new', element: <NotificationRuleNewPage /> },
      { path: '/agents/*', element: <Navigate to="/configure?type=custom" replace /> },
      { path: '/tags', element: <TagsPage /> },
      {
        path: '/notifications',
        element: <NotificationsPage />
      },
      {
        path: '/settings',
        element: <SettingsPage />
      },
      {
        path: '/users',
        element: <UsersPage section="users" />
      },
      {
        path: '/users/role-assignments',
        element: <UsersPage section="role-assignments" />
      },
      {
        path: '/users/groups',
        element: <UsersPage section="groups" />
      },
      {
        path: '/feedback',
        element: <FeedbackPage />
      },
      {
        path: '/usage',
        element: <UsageAnalyticsPage />
      },
      // Merge review: preview-first, resolve-inline. The topbar/branch-row Merge
      // actions navigate here (?source=&target=) instead of opening a modal.
      {
        path: '/branches/merge',
        element: <MergeReviewPage />
      },
      // The durable "merges in progress" list — resume a saved review session.
      {
        path: '/branches/merges',
        element: <MergesPage />
      },
      // Documents library owns `/documents`: living markdown, versioned
      // binaries, and report templates (nested tab, same shell as Dashboards).
      // Selection lives on path segments (`/documents/<path>`, `/documents/f/<folder>`,
      // `/documents/templates/<path>`). Legacy `/docs` bookmarks redirect here.
      {
        path: '/documents',
        element: <DocumentsHomePage />,
        children: [
          { index: true, element: <DocsHubPage /> },
          { path: 'templates', element: <DocTemplatesExplorerPage /> },
          { path: 'templates/f/*', element: <DocTemplatesExplorerPage /> },
          { path: 'templates/*', element: <DocTemplatesExplorerPage /> },
          { path: 'f/*', element: <DocsHubPage /> },
          { path: '*', element: <DocsHubPage /> }
        ]
      },
      { path: '/docs', element: <LegacyDocsRedirect /> },
      { path: '/docs/*', element: <LegacyDocsRedirect /> },
      {
        path: '/guide',
        element: (
          <DebugRoute fallbackHref={PLANT_HREF.root}>
            <GuidePage />
          </DebugRoute>
        )
      },
      {
        path: '/releases',
        element: <ReleaseHistoryPage />
      },
      // Legal documents (linked from the user guide's "Legal and Privacy"
      // section). Rendered from the markdown sources in the repo-root docs/
      // folder via the same renderer as the user guide.
      {
        path: '/legal/privacy-policy',
        element: <PrivacyPolicyPage />
      },
      {
        path: '/legal/terms-of-use',
        element: <TermsOfUsePage />
      },
      {
        path: '/agent-memory',
        element: <Navigate to={CHAT_MEMORY_PATH} replace />
      }
    ]
  },
  {
    path: '*',
    element: <NotFoundPage href={PLANT_HREF.root} />
  }
]

export default routes
